import React from 'react';
import { Form, Formik } from 'formik';
import {
  Button,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  TextInput,
  Title,
} from '@mantine/core';
import * as Yup from 'yup';

interface OdooConfigurationEditFormProps {
  onSubmit: (values: {
    url: string;
    db: string;
    name: string;
    username: string;
    password: string;
  }) => Promise<void>;
  onCancel: () => void;
  initialValues: {
    url: string;
    db: string;
    name: string;
    username: string;
    password?: string;
  };
}

const OdooConfigurationSchema = Yup.object().shape({
  url: Yup.string().required('URL is required').url('Must be a valid URL'),
  db: Yup.string().required('Database name is required'),
  name: Yup.string().required('Name is required'),
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

export const OdooConfigurationEditForm: React.FC<
  OdooConfigurationEditFormProps
> = ({ onSubmit, onCancel, initialValues }) => {
  return (
    <Formik
      initialValues={{
        url: initialValues.url,
        db: initialValues.db,
        name: initialValues.name,
        username: initialValues.username,
        password: initialValues.password || '',
      }}
      validationSchema={OdooConfigurationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, getFieldProps, touched, errors }) => (
        <Form>
          <Stack gap="md">
            <Title order={4}>Odoo Configuration</Title>
            <Divider />
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput
                label="Odoo URL"
                withAsterisk
                placeholder="https://odoo.example.com"
                {...getFieldProps('url')}
                error={touched.url && errors.url}
              />
              <TextInput
                label="Database Name"
                withAsterisk
                placeholder="database_name"
                {...getFieldProps('db')}
                error={touched.db && errors.db}
              />
              <TextInput
                label="Name"
                withAsterisk
                placeholder="Odoo Configuration"
                {...getFieldProps('name')}
                error={touched.name && errors.name}
              />
              <TextInput
                label="Username"
                withAsterisk
                placeholder="admin"
                {...getFieldProps('username')}
                error={touched.username && errors.username}
              />
              <TextInput
                label="Password"
                withAsterisk
                placeholder="Enter password"
                {...getFieldProps('password')}
                error={touched.password && errors.password}
              />
            </SimpleGrid>

            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                Update
              </Button>
            </Group>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
